import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {getInboxes} from '../../data/inboxes/inboxesSelectors';
import {getCustomViews} from '../../data/preferences/customViewsSelectors';
import {getUserId} from '../../data/teammates/teammatesSelectors';
import {InboxesParameters, InboxesParametersTypesEnum} from '../inboxes/parameters/inboxesParameters';
import {findParametersTeammateId} from '../inboxes/parameters/inboxesParametersHelpers';
import {getCellsPanel, getFlatPanels, getPanels} from '../panelsSelectors';
import {panelIsCellsPanel} from '../panelsState';
import {CellsPanelId, PanelTypesEnum} from '../panelStateBase';
import {CellsPanelPageState, CellsPanelState} from './cellsPanelState';

/*
 * Cells panel.
 */

export interface CellsPanelProps {
  panelId: CellsPanelId;
}

export type CellsPanelIdSelector<P> = AppParametricSelector<P, CellsPanelId>;

function getCellsPanelId(_state: unknown, props: CellsPanelProps) {
  return props.panelId;
}

export type CellsPanelSelector<P> = AppParametricSelector<P, CellsPanelState>;

export function makeGetCellsPanel(): CellsPanelSelector<CellsPanelProps>;
export function makeGetCellsPanel<P>(getPanelId: CellsPanelIdSelector<P>): CellsPanelSelector<P>;
export function makeGetCellsPanel(getPanelId: CellsPanelIdSelector<any> = getCellsPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    // A corresponding panel should exist, otherwise we have a problem.
    if (!panel || panel.type !== PanelTypesEnum.CELLS) {
      throw new Error('This cells panel could not be found.');
    }

    return panel;
  });
}

export type CellsPanelPageSelector<P> = AppParametricSelector<P, CellsPanelPageState>;

export function makeGetCellsPanelPage(): CellsPanelPageSelector<CellsPanelProps>;
export function makeGetCellsPanelPage<P>(getPanelId: CellsPanelIdSelector<P>): CellsPanelPageSelector<P>;
export function makeGetCellsPanelPage(getPanelId: CellsPanelIdSelector<any> = getCellsPanelId) {
  return createSelector(makeGetCellsPanel(getPanelId), ({pages}) => pages.next ?? pages.current);
}

export function makeGetOptionalCellsPanelPage<P>(
  getPanelId: AppParametricSelector<P, CellsPanelId | undefined>,
) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    if (!panelId) {
      return undefined;
    }

    const panel = panels[panelId];
    if (panel?.type !== PanelTypesEnum.CELLS) {
      return undefined;
    }

    return panel.pages.current;
  });
}

export function makeGetCellsPanelCurrentPage(): CellsPanelPageSelector<CellsPanelProps>;
export function makeGetCellsPanelCurrentPage<P>(
  getPanelId: CellsPanelIdSelector<P>,
): CellsPanelPageSelector<P>;
export function makeGetCellsPanelCurrentPage(getPanelId: CellsPanelIdSelector<any> = getCellsPanelId) {
  return createSelector(makeGetCellsPanel(getPanelId), ({pages}) => pages.current);
}

export type CellsPanelPageParametersSelector<P> = AppParametricSelector<P, CellsPanelPageState['parameters']>;

/*
 * Cells panel rooms.
 */

function findParametersRoom(userId: number, teammateId: number, parameters: InboxesParameters) {
  // While searching, subscribe to all conversations the teammate has access to.
  if (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES && parameters.search) {
    return `globalfolder-${userId}`;
  }

  switch (parameters.type) {
    // Subscribe to all conversations the user or delegated teammate is a tracker on.
    case InboxesParametersTypesEnum.USER:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return `teammatefolder-${teammateId}`;

    // Subscribe to all conversations in the inbox.
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.INBOX:
      return `inboxfolder-${parameters.inboxId}`;

    // Subscribe to all conversations the user or delegated teammate has access to.
    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAM:
    case InboxesParametersTypesEnum.GLOBAL:
    case InboxesParametersTypesEnum.TEAMMATE:
      return `globalfolder-${teammateId}`;

    // TODO-LENS: ideally we subscribe to more fine-grained inbox rooms corresponding to the inboxes of the lens.
    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
      return `globalfolder-${userId}`;

    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return undefined;

    default:
      throw new UnreachableError(parameters);
  }
}

const getCellsPanelsParameters = createSelector(getFlatPanels, (panels) =>
  chain(panels)
    .map((panel) => panel.type === PanelTypesEnum.CELLS && panel.pages)
    .compact()
    .map((pages) => [pages.current, pages.next])
    .flatten()
    .compact()
    .map((page) => page.parameters)
    .uniq()
    .value(),
);

export const getCellsPanelsRooms = createSelector(
  [getUserId, getInboxes, getCustomViews, wrapInShallowSelector(getCellsPanelsParameters)],
  (userId, inboxes, customViews, cellsPanelsParameters) =>
    chain(cellsPanelsParameters)
      .map((parameters) => {
        const teammateId = findParametersTeammateId(userId, inboxes, customViews, parameters);
        return findParametersRoom(userId, teammateId, parameters);
      })
      .compact()
      .value(),
);

export const getCellsPanelContexts = createSelector(
  [getUserId, getInboxes, getCustomViews, getFlatPanels],
  (userId, inboxes, customViews, panels) =>
    chain(panels)
      .filter(panelIsCellsPanel)
      .map((panel) => panel.pages)
      .flatMap((pages) => [pages.current, pages.next])
      .compact()
      .map((page) => {
        const teammateId = findParametersTeammateId(userId, inboxes, customViews, page.parameters);
        return {teammateId, page};
      })
      .value(),
);

export const getCurrentCellsPanelCellsContext = createSelector(
  [getCellsPanel],
  (cellsPanel) => cellsPanel?.pages?.current?.cellsContext,
);
